package ru.villawonka.app.presentation.screens.settings.viewmodel

import ru.villawonka.app.AppConfig
import ru.villawonka.app.BuildConfig
import ru.villawonka.app.common.mvvm.BaseViewModel
import ru.villawonka.app.common.utils.fold
import ru.villawonka.app.domain.model.CountryDomain
import ru.villawonka.app.domain.model.CurrencyDomain
import ru.villawonka.app.domain.model.LanguageDomain
import ru.villawonka.app.domain.model.UserSettingsDomain
import ru.villawonka.app.domain.usecase.GetAvailableCountriesUseCase
import ru.villawonka.app.domain.usecase.GetAvailableCurrenciesUseCase
import ru.villawonka.app.domain.usecase.GetAvailableLanguagesUseCase
import ru.villawonka.app.domain.usecase.GetUserSettingsUseCase
import ru.villawonka.app.domain.usecase.UpdateUserSettingsUseCase
import ru.villawonka.app.presentation.model.CountryUIModel
import ru.villawonka.app.presentation.model.CurrencyUIModel
import ru.villawonka.app.presentation.model.LanguageUIModel
import ru.villawonka.app.presentation.model.toDomainModel
import ru.villawonka.app.presentation.model.toUIModel

class SettingsViewModel(
    private val getUserSettingsUseCase: GetUserSettingsUseCase,
    private val updateUserSettingsUseCase: UpdateUserSettingsUseCase,
    private val getAvailableLanguagesUseCase: GetAvailableLanguagesUseCase,
    private val getAvailableCountriesUseCase: GetAvailableCountriesUseCase,
    private val getAvailableCurrenciesUseCase: GetAvailableCurrenciesUseCase,
) : BaseViewModel<SettingsUIState>() {

    private var currentUserSettings: UserSettingsDomain? = null

    private val availableLanguages = mutableListOf<LanguageDomain>()
    private val availableCountries = mutableListOf<CountryDomain>()
    private val availableCurrencies = mutableListOf<CurrencyDomain>()

    init {
        loadScreen()
    }

    override fun createInitialState() = SettingsUIState.Loading

    fun onRetryButtonClick() = launch { loadScreen() }

    fun onLanguageMenuItemClick() = launch {
        currentUserSettings?.let {
            shootEvent(
                SettingsUIEvent.OpenLanguagePicker(
                    selectedItem = it.language.toUIModel(),
                    items = availableLanguages.map { it.toUIModel() }
                )
            )
        }
    }

    fun onCountryMenuItemClick() = launch {
        currentUserSettings?.let {
            shootEvent(
                SettingsUIEvent.OpenCountryPicker(
                    selectedItem = it.country.toUIModel(),
                    items = availableCountries.map { it.toUIModel() }
                )
            )
        }
    }

    fun onCurrencyMenuItemClick() = launch {
        currentUserSettings?.let {
            shootEvent(
                SettingsUIEvent.OpenCurrencyPicker(
                    selectedItem = it.currency.toUIModel(),
                    items = availableCurrencies.map { it.toUIModel() }
                )
            )
        }
    }

    fun onServiceNewsItemClick() = launch {
        shootEvent(SettingsUIEvent.OpenServiceNews)
    }

    fun onContactSupportItemClick() = launch {
        shootEvent(SettingsUIEvent.OpenContactSupport)
    }

    fun onPrivacyPolicyItemClick() = launch {
        shootEvent(SettingsUIEvent.OpenPrivacyPolicy)
    }

    fun onTermsOfUseItemClick() = launch {
        shootEvent(SettingsUIEvent.OpenTermsOfUse)
    }

    fun onLanguageWasChanged(language: LanguageUIModel) {
        val languageDomain = language.toDomainModel()
        updateSettings(update = { it.copy(language = languageDomain) })
    }

    fun onCurrencyWasChanged(currency: CurrencyUIModel) =
        updateSettings(update = { it.copy(currency = currency.toDomainModel()) })

    fun onCountryWasChanged(country: CountryUIModel) =
        updateSettings(update = { it.copy(country = country.toDomainModel()) })

    private fun loadScreen() = launch {
        setState(SettingsUIState.Loading)

        availableCountries.clear()
        availableCurrencies.clear()
        availableLanguages.clear()

        availableCountries.addAll(getAvailableCountriesUseCase())
        availableCurrencies.addAll(getAvailableCurrenciesUseCase())
        availableLanguages.addAll(getAvailableLanguagesUseCase())

        getUserSettingsUseCase().fold(
            ifLeft = { setState(SettingsUIState.Error(it)) },
            ifRight = { userSettings ->
                currentUserSettings = userSettings
                setState(SettingsUIState.Data(
                    language = userSettings.language.toUIModel(),
                    country = userSettings.country.toUIModel(),
                    currency = userSettings.currency.toUIModel(),
                    appVersion = "${AppConfig.APP_VERSION}-${BuildConfig.buildType}"
                ))
            }
        )
    }

    private fun updateSettings(update: (UserSettingsDomain) -> UserSettingsDomain) = launch {
        val settings = currentUserSettings ?: return@launch
        currentUserSettings = update(settings).also { newSettings ->
            updateUserSettingsUseCase(newSettings).fold(
                ifLeft = { setState(SettingsUIState.Error(it)) },
                ifRight = {
                    (currentStateValue as? SettingsUIState.Data)?.let { state ->
                        setState(state.copy(
                            language = newSettings.language.toUIModel(),
                            country = newSettings.country.toUIModel(),
                            currency = newSettings.currency.toUIModel(),
                        ))
                    }
                }
            )
        }
    }
}